/**
 * Admin機能のMixpanel分析設定
 *
 * このファイルにはadmin専用機能のフォーム設定を定義します：
 * フォーム送信が“ユーザー価値の発生”に繋がりそうなものを中心に設定してください
 */

import type { TrackedFormMeta } from '@/constants/mixpanel/types'

import { Feature, Action, EventName } from '@/constants/mixpanel/constants'

/**
 * 管理者機能のフォーム分析設定
 */
export const AdminForms = {
  // コンテナ関連
  Container: {
    Create: {
      feature: Feature.Container,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'container_create',
    } as TrackedFormMeta,
    Update: {
      feature: Feature.Container,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'container_update',
    } as TrackedFormMeta,
  },

  // Shipment詳細関連（Admin専用）
  ShipmentAdmin: {
    BookingStatus: {
      feature: Feature.Shipment,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'booking_status',
    } as TrackedFormMeta,
    Detail: {
      feature: Feature.Shipment,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'shipment_detail',
    } as TrackedFormMeta,
    Update: {
      feature: Feature.Shipment,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'shipment_update',
    } as TrackedFormMeta,
    Other: {
      feature: Feature.Shipment,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'shipment_other',
    } as TrackedFormMeta,
    SelectQuote: {
      feature: Feature.Shipment,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'shipment_select_quote',
    } as TrackedFormMeta,
  },

  // 航空貨物関連
  AirTransportCargo: {
    Upsert: {
      feature: Feature.AirTransportCargo,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'air_transport_cargo_upsert',
    } as TrackedFormMeta,
  },

  // A/N関連
  ArrivalNotices: {
    Upsert: {
      feature: Feature.ArrivalNotices,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'arrival_notices_upsert',
    } as TrackedFormMeta,
    SendEmail: {
      feature: Feature.ArrivalNotices,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'arrival_notices_send_email',
    } as TrackedFormMeta,
  },

  // BookingNote関連
  BookingNote: {
    Upsert: {
      feature: Feature.BookingNote,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'booking_note_upsert',
    } as TrackedFormMeta,
  },

  // 通関依頼書関連
  CustomsClearance: {
    Upsert: {
      feature: Feature.CustomsClearance,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'customs_clearance_upsert',
    } as TrackedFormMeta,
  },

  // 配送依頼書関連
  Delivery: {
    Create: {
      feature: Feature.Delivery,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'delivery_create',
    } as TrackedFormMeta,
    Update: {
      feature: Feature.Delivery,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'delivery_update',
    } as TrackedFormMeta,
  },

  // 配送情報関連
  TransportRequest: {
    Upsert: {
      feature: Feature.TransportRequest,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'transport_request_upsert',
    } as TrackedFormMeta,
  },

  // PortrichのBL関連
  PortrichBillOfLadings: {
    Upsert: {
      feature: Feature.PortrichBillOfLadings,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'portrich_bill_of_ladings_upsert',
    } as TrackedFormMeta,
  },

  // S/I関連
  ShippingInstructions: {
    Upsert: {
      feature: Feature.ShippingInstructions,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'shipping_instructions_upsert',
    } as TrackedFormMeta,
  },

  // デビットノート・クレジットノート関連
  DebitCreditNote: {
    Create: {
      feature: Feature.DebitCreditNote,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'debit_credit_note_create',
    } as TrackedFormMeta,
    Detail: {
      feature: Feature.DebitCreditNote,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'debit_credit_note_detail_upsert',
    } as TrackedFormMeta,
  },

  // P/S関連
  ProfitShare: {
    Update: {
      feature: Feature.ProfitShare,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'profit_share_dialog',
    } as TrackedFormMeta,
  },

  // Task関連
  Task: {
    Alias: {
      feature: Feature.Task,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'task_alias',
    } as TrackedFormMeta,
    Create: {
      feature: Feature.Task,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'task_create',
    } as TrackedFormMeta,
    Deadline: {
      feature: Feature.Task,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'task_deadline',
    } as TrackedFormMeta,
  },

  // スケジュール関連
  Progress: {
    Flight: {
      feature: Feature.Progress,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'progress_flight',
    } as TrackedFormMeta,
    Vessel: {
      feature: Feature.Progress,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'progress_vessel',
    } as TrackedFormMeta,
    Customs: {
      feature: Feature.Progress,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'progress_customs',
    } as TrackedFormMeta,
  },

  // 請求書関連
  Invoice: {
    PaymentForm: {
      feature: Feature.Invoice,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'create_invoice',
    } as TrackedFormMeta,
    PriceForm: {
      feature: Feature.Invoice,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'invoice_price_upsert',
    } as TrackedFormMeta,
  },

  // フォワーダー関連
  Forwarder: {
    Registration: {
      feature: Feature.Forwarder,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'forwarder_registration',
    } as TrackedFormMeta,
  },
} as const
